import styled from "styled-components";

/**
 * A 1:1 reproduction of the Felix app's voucher screen.
 *
 * Every value below is taken from
 * `felix_app/lib/pages/home_page/vouchers_page/voucher_page.dart` — the same
 * colours, type sizes, weights, radii and gaps — so that what a merchant sees
 * while building a voucher is what the customer will actually get. Where the
 * Dart source is the origin of a number it is named in a comment.
 */

/* voucher_page.dart: _bgColor / _lime / _darkText / _greyText / _cardBg */
const BG = "#EDEFF1";
const LIME = "#C7FF00";
const DARK_TEXT = "#0F172A";
const GREY_TEXT = "#4C4C60";
const CARD_BG = "#F9FBFC";

export const Phone = styled.div`
  /* The device shell is dashboard chrome, not part of the app screen. */
  box-shadow: 0 0 0 3px #fff, 0 0 0 10px #eaeaea, 0 0 0 15px #fff;
  border-radius: 28px;
  background: ${BG};
  width: 320px;
  max-width: 100%;
  height: 640px;
  margin-top: 15px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  color: ${DARK_TEXT};
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    sans-serif;
`;

/* Scaffold header: Padding.fromLTRB(16, 12, 16, 0) */
export const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px 0;
  flex: none;
`;

/* Material(color: black, shape: CircleBorder) + EdgeInsets.all(10), icon 18 */
export const BackButton = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #000;
  color: ${LIME};
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;

  svg {
    width: 18px;
    height: 18px;
  }
`;

/* Expanded + textAlign.center, _inter(size: 16, weight: w600) */
export const HeaderTitle = styled.div`
  flex: 1;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: ${DARK_TEXT};
`;

/* 44.widthBox — balances the back button so the title stays centred. */
export const HeaderSpacer = styled.div`
  width: 44px;
  flex: none;
`;

/* SingleChildScrollView, padding.fromLTRB(16, 16, 16, 24) */
export const Scroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 16px 24px;

  /* The phone screen has no scrollbar; this is a desktop affordance, so keep
     it faint enough not to read as part of the app's UI. The dashboard's own
     scrollbar colours are dark and would sit as a hard bar on this light
     surface. */
  scrollbar-width: thin;
  scrollbar-color: rgba(15, 23, 42, 0.16) transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(15, 23, 42, 0.16);
    border: none;
    border-radius: 999px;
  }
`;

/* ClipRRect(borderRadius: 20) wrapping a SizedBox(height: 220) */
export const Media = styled.div`
  position: relative;
  height: 220px;
  border-radius: 20px;
  overflow: hidden;
  /* images.isEmpty -> Container(color: Colors.black12) */
  background: rgba(0, 0, 0, 0.12);

  .swiper,
  .swiper-wrapper,
  .swiper-slide {
    height: 100%;
  }

  img {
    width: 100%;
    height: 220px;
    object-fit: cover;
    display: block;
  }
`;

/* Positioned(left: 12, top: 12), white, radius 6, padding h10 v5, 11/w600 */
export const MediaBadge = styled.span`
  position: absolute;
  left: 12px;
  top: 12px;
  z-index: 2;
  background: #fff;
  border-radius: 6px;
  padding: 5px 10px;
  font-size: 11px;
  font-weight: 600;
  color: ${DARK_TEXT};
`;

/* Positioned(right: 12, top: 12), black fill, white text */
export const SoldOutBadge = styled(MediaBadge)`
  left: auto;
  right: 12px;
  background: #000;
  color: #fff;
`;

/* AnimatedSmoothIndicator: dotWidth/Height 7, spacing 4, 12.heightBox above */
export const Dots = styled.div`
  display: flex;
  justify-content: center;
  gap: 4px;
  margin-top: 12px;

  span {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: rgba(76, 76, 96, 0.25);
  }
  span.is-active {
    background: ${GREY_TEXT};
  }
`;

/* 20.heightBox, then Row(crossAxisAlignment: start) */
export const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  margin-top: 20px;
`;

export const TitleBlock = styled.div`
  flex: 1;
  min-width: 0;
`;

/* _inter(size: 20, weight: w700) */
export const VoucherName = styled.h2`
  font-size: 20px;
  font-weight: 700;
  line-height: 1.25;
  margin: 0;
  color: ${DARK_TEXT};
`;

/* 4.heightBox, _inter(size: 13, weight: w400, color: _greyText) */
export const MerchantName = styled.p`
  font-size: 13px;
  font-weight: 400;
  color: ${GREY_TEXT};
  margin: 4px 0 0;
`;

/* 4.heightBox, Icon(place_outlined, size 13) + 4.widthBox + 12/w400 grey */
export const Location = styled.p`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  font-size: 12px;
  font-weight: 400;
  color: ${GREY_TEXT};
  margin: 4px 0 0;

  svg {
    width: 13px;
    height: 13px;
    flex: none;
    margin-top: 1px;
  }
`;

/* 12.widthBox + ClipRRect(radius 12) around a 48x48 image */
export const MerchantMark = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  object-fit: cover;
  margin-left: 12px;
  flex: none;
`;

/* Wrap(spacing: 10, runSpacing: 10) after 20.heightBox */
export const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
`;

/* Wrap(spacing: 8, runSpacing: 8) — used by "At this location" */
export const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
`;

/* _InfoChip: padding h12 v8, _cardBg, radius 100, icon 14, gap 6, 12/w500 */
export const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: ${CARD_BG};
  border-radius: 100px;
  font-size: 12px;
  font-weight: 500;
  color: ${DARK_TEXT};
  white-space: nowrap;

  svg,
  img {
    width: 14px;
    height: 14px;
    flex: none;
  }
  img {
    border-radius: 4px;
    object-fit: cover;
  }
`;

/* Redeemed banner: _lime @ 35%, radius 14, padding h14 v12, icon 18, gap 10 */
export const RedeemedBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  padding: 12px 14px;
  border-radius: 14px;
  background: rgba(199, 255, 0, 0.35);
  font-size: 13px;
  font-weight: 600;
  color: ${DARK_TEXT};

  svg {
    width: 18px;
    height: 18px;
    flex: none;
    color: rgba(0, 0, 0, 0.87);
  }
`;

/* 24.heightBox before each section, heading _inter(size: 15, weight: w600) */
export const Section = styled.section`
  margin-top: 24px;
`;

export const SectionTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: ${DARK_TEXT};
  margin: 0;
`;

/* 8.heightBox, _inter(size: 13, weight: w400, color: _greyText) */
export const Body = styled.p`
  font-size: 13px;
  font-weight: 400;
  color: ${GREY_TEXT};
  margin: 8px 0 0;
  line-height: 1.5;
`;

/* Licence lines: _cardBg, radius 14, padding 14, each line 12/w500 grey v3 */
export const LicenceCard = styled.div`
  margin-top: 12px;
  padding: 14px;
  background: ${CARD_BG};
  border-radius: 14px;

  p {
    font-size: 12px;
    font-weight: 500;
    color: ${GREY_TEXT};
    margin: 0;
    padding: 3px 0;
  }
`;

/* Opening hours: _cardBg, radius 16, padding h16 v4, rows 13/w500 + 13/w400 */
export const HoursCard = styled.div`
  margin-top: 10px;
  padding: 4px 16px;
  background: ${CARD_BG};
  border-radius: 16px;
`;

export const HoursRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  font-size: 13px;

  & + & {
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }

  .day {
    font-weight: 500;
    color: ${DARK_TEXT};
  }
  .hours {
    font-weight: 400;
    color: ${GREY_TEXT};
  }
`;

/* Included plans: _cardBg, radius 100, padding h12 v8, 12/w500 */
export const PlanPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  background: ${CARD_BG};
  border-radius: 100px;
  font-size: 12px;
  font-weight: 500;
  color: ${DARK_TEXT};
`;

/* SafeArea(minimum: fromLTRB(16, 0, 16, 16)) + height 51, radius 20, 15/w600 */
export const Cta = styled.div`
  flex: none;
  margin: 0 16px 16px;
  height: 51px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 600;
  color: ${DARK_TEXT};
  background: ${(props) =>
    props.$exhausted ? "rgba(76, 76, 96, 0.3)" : LIME};
`;
